<template>
  <v-container>
    <v-row
      align="center"
    >
      <v-col
        cols="auto"
      >
        <div class="title text-uppercase font-weight-medium">
          Browse Users
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-text-field
          v-model="filters.email"
          solo
          placeholder="Search"
          flat
          dense
          hide-details
          clearable
          append-icon="fas fa-search"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          dark
          color="primary"
          class="ma-0"
          @click="editUserDialog.user = undefined; editUserDialog.visible = true"
        >
          <v-icon
            left
            small
          >
            fas fa-plus
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!--<editor-fold desc="Filters">-->
      <filters-column
        :filters-active="filtersActive"
        @filters:clear-all="clearFilters()"
      >
        <v-subheader class="font-weight-medium">
          Client
        </v-subheader>
        <v-autocomplete
          v-model="filters.client_id"
          solo
          flat
          outlined
          :items="clients.data"
          :loading="clients.loading"
          clearable
          item-text="name"
          item-value="id"
          placeholder="Select client..."
          dense
        />
        <v-subheader class="font-weight-medium">
          Domain
        </v-subheader>
        <v-autocomplete
          v-model="filters.domain"
          solo
          flat
          outlined
          :items="domains.data"
          :loading="domains.loading"
          clearable
          item-text="domain"
          item-value="domain"
          placeholder="Select domain..."
          dense
        />
      </filters-column>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Table">-->
      <v-col>
        <pagination
          :page.sync="items.pagination.page"
          :last-page="items.meta.last_page"
          :total="items.meta.total"
          :from="items.meta.from"
          :to="items.meta.to"
        />
        <v-data-table
          :headers="headers"
          :items="items.data"
          :server-items-length="items.meta.total"
          :sort-by.sync="items.sorting.sortBy"
          :sort-desc.sync="items.sorting.desc"
          :page.sync="items.pagination.page"
          :items-per-page="items.pagination.perPage"
          :loading="items.loading"
          hide-default-footer
          must-sort
          class="v-data-table--striped elevation-15"
          @update:sort-by="fetchItems()"
          @update:page="fetchItems()"
        >
          <template v-slot:item="{ item: user }">
            <tr>
              <td>
                <div class="subtitle-2 primary--text">
                  {{ user.name }}
                </div>
              </td>
              <td>
                {{ user.username }}
              </td>
              <td>
                {{ user.email }}
              </td>
              <td>
                <span v-if="user.client">{{ user.client.name }}</span>
                <span
                  v-else
                  class="grey--text"
                >None</span>
              </td>
              <td>
                {{ user.role ? user.role.title : 'n/a' }}
              </td>
              <td class="text-no-wrap">
                {{ $dayjs(user.created_at).format('DD.MM.YYYY. HH:mm:ss') }}
              </td>
              <td
                class="text-no-wrap"
                style="width: 1px"
              >
                <v-menu
                  offset-y
                  min-width="100px"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="grey"
                      small
                      v-on="on"
                    >
                      <v-icon small>
                        fa fa-ellipsis-v
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list
                    dense
                    class="body-2"
                  >
                    <v-list-item @click="editUserDialog.user = user; editUserDialog.visible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-pen
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Edit Details</v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="editUserDialog.user = user; deleteConfirmationDialogVisible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-trash
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Delete User</v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div class="pa-2">
          <pagination
            :page.sync="items.pagination.page"
            :last-page="items.meta.last_page"
            :total="items.meta.total"
            :from="items.meta.from"
            :to="items.meta.to"
          />
        </div>
      </v-col>
    </v-row>

    <edit-user-dialog
      :visible.sync="editUserDialog.visible"
      :user="editUserDialog.user"
      @user:updated="fetchItems()"
      @user:created="fetchItems()"
      @user:deleted="fetchItems()"
    />

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deleteUser(editUserDialog.user)"
    />
  </v-container>
</template>

<script>
import fetchableItemsMixin from '@/mixins/fetchableItemsMixin'
import FiltersColumn from '@/components/helpers/FiltersColumn'
import { cloneDeep } from 'lodash'
import UserModel from '@/models/UserModel'
import Users from '@/services/Users'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import Clients from '@/services/Clients'
import EditUserDialog from '@/components/users/EditUserDialog'

export default {
  name: 'Users',
  components: {
    EditUserDialog,
    ConfirmDeleteDialog,
    FiltersColumn
  },
  mixins: [fetchableItemsMixin, deleteConfirmationMixin],
  data() {
    return {
      clients: {
        loading: false,
        data: []
      },
      editUserDialog: {
        visible: false,
        user: undefined
      },
      domains: {
        loading: false,
        data: [
          { domain: 'jukeboxmusic.eu' },
          { domain: 'jukebox.hr' },
          { domain: 'musicboxbasic.com' }
        ]
      }
    }
  },
  watch: {
    'filters.client_id': function () {
      this.fetchItems()
    }
  },
  beforeCreate() {
    this.emptyFilters = cloneDeep(UserModel.emptyFilters)
  },
  created() {
    this.headers = [
      { text: 'Name', value: 'name' },
      { text: 'Username', value: 'username' },
      { text: 'Email', value: 'email' },
      { text: 'Client', value: 'client_id' },
      { text: 'Role', value: 'role' },
      { text: 'Created At', value: 'created_at' },
      { sortable: false }
    ]

    this.fetchClients()
  },
  methods: {
    async itemsFetcher(queryParams) {
      return await Users.fetch(queryParams)
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    },
    async deleteUser(user) {
      this.deleting = true

      try {
        await Users.delete(user.id)

        this.$store.dispatch('alert', {
          text: 'User successfuly deleted',
          color: 'success'
        })

        this.fetchItems()
      } catch (e) {
        console.error(e)
      } finally {
        this.deleting = false
        this.deleteConfirmationDialogVisible = false
      }
    }
  }
}
</script>
